import { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../stores/useStore';
import fetchWithTenant from '../utils/fetchWithTenant';
import { AlertCircle } from 'lucide-react';

/**
 * MilestonePlayer - Auto-selects streaming mode:
 *   direct: Tailscale/direct - REST signaling (POST session + PATCH answer)
 *   bridge: Agent-based - WebSocket signaling via Pion bridge
 */
const MilestonePlayer = ({ camera, integrationId, onFallback, positionMs }) => {
  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const mountedRef = useRef(true);
  const icePollingRef = useRef(null);
  const sessionDataRef = useRef(null);

  const cleanup = useCallback(() => {
    if (icePollingRef.current) { clearInterval(icePollingRef.current); icePollingRef.current = null; }
    if (wsRef.current) {
      wsRef.current.onmessage = null;
      wsRef.current.onclose = null;
      try { wsRef.current.close(); } catch(e) {}
      wsRef.current = null;
    }
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => { mountedRef.current = false; cleanup(); };
  }, [camera?.id, integrationId]);

  const connect = async () => {
    if (!camera?.id || !integrationId) return;
    setStatus('connecting');
    setError(null);
    cleanup();
    try {
      const modeRes = await fetchWithTenant('/api/milestone/' + integrationId + '/webrtc/mode');
      const { mode } = modeRes.ok ? await modeRes.json() : { mode: 'direct' };
      if (mode === 'bridge') { await connectBridge(); } else { await connectDirect(); }
    } catch (err) {
      console.error('[MilestonePlayer] Error:', err);
      if (mountedRef.current) { setStatus('error'); setError(err.message); if (onFallback) onFallback(); }
    }
  };

  const connectDirect = async () => {
    setStatus('negotiating');
    let iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
    try {
      const t = await fetch('/api/turn/credentials', { credentials: 'include' });
      if (t.ok) { const d = await t.json(); if (d.iceServers) iceServers = d.iceServers; }
    } catch(e) {}

    const sessionRes = await fetchWithTenant('/api/milestone/' + integrationId + '/webrtc/session', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cameraId: camera.id, positionMs: positionMs || null })
    });
    if (!sessionRes.ok) { const e = await sessionRes.json().catch(() => ({})); throw new Error(e.error || 'Session failed: ' + sessionRes.status); }
    const sessionData = await sessionRes.json();
    if (!sessionData.offerSDP) throw new Error('No offerSDP from Milestone');
    sessionDataRef.current = sessionData;
    if (sessionData.iceServers?.length) {
      iceServers = [...iceServers, ...sessionData.iceServers.map(s => ({ urls: s.url, username: s.userName, credential: s.credential }))];
    }
    if (!mountedRef.current) return;

    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;
    // Send ICE candidates to Milestone as they're gathered (like official sample)
    pc.onicecandidate = e => {
      if (e.candidate && sessionDataRef.current) {
        fetchWithTenant('/api/milestone/' + integrationId + '/webrtc/ice/' + sessionDataRef.current.sessionId, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ candidates: [JSON.stringify(e.candidate)] })
        }).catch(() => {});
      }
    };
    pc.ontrack = e => {
      if (!mountedRef.current) return;
      if (videoRef.current && e.streams[0]) {
        videoRef.current.srcObject = e.streams[0];
        setStatus('playing');
        // Capture thumbnail frame once video has actual frame data
        const captureFrame = () => {
          const video = videoRef.current;
          if (!video || !video.videoWidth || !video.videoHeight) {
            setTimeout(captureFrame, 500);
            return;
          }
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d').drawImage(video, 0, 0);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            if (dataUrl && dataUrl.length > 1000) {
              useStore.getState().setMilestoneThumbnail(camera.id, dataUrl);
              console.log('[MilestonePlayer] Thumbnail captured for', camera.id);
            } else {
              setTimeout(captureFrame, 500);
            }
          } catch(err) { console.warn('[MilestonePlayer] Capture failed:', err); }
        };
        setTimeout(captureFrame, 1000);
      }
    };
    pc.oniceconnectionstatechange = () => {
      if ((pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') && mountedRef.current) {
        setStatus('error'); setError('Connection lost'); if (onFallback) onFallback();
      }
    };

    // Milestone returns offerSDP as JSON string {"type":"offer","sdp":"..."} — parse directly
    const offerObj = typeof sessionData.offerSDP === 'string' ? JSON.parse(sessionData.offerSDP) : sessionData.offerSDP;
    await pc.setRemoteDescription(offerObj);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    // Send answer immediately — ICE candidates go separately via /webrtc/ice/

    const answerRes = await fetchWithTenant('/api/milestone/' + integrationId + '/webrtc/session/' + sessionData.sessionId, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answerSDP: JSON.stringify(pc.localDescription) })
    });
    if (!answerRes.ok) { const e = await answerRes.json().catch(() => ({})); throw new Error(e.error || 'Answer failed: ' + answerRes.status); }



    let pollCount = 0;
    icePollingRef.current = setInterval(async () => {
      if (!mountedRef.current || pollCount > 20) { clearInterval(icePollingRef.current); return; }
      pollCount++;
      try {
        const r = await fetchWithTenant('/api/milestone/' + integrationId + '/webrtc/ice/' + sessionData.sessionId);
        if (r.ok) {
          const d = await r.json();
          for (const c of (d.candidates || [])) {
            try { await pc.addIceCandidate(new RTCIceCandidate({ candidate: c, sdpMLineIndex: 0 })); } catch(e) {}
          }
        }
      } catch(e) {}
    }, 1000);
  };

  const connectBridge = async () => {
    const streamId = crypto.randomUUID();
    const bridgeRes = await fetchWithTenant('/api/milestone/' + integrationId + '/webrtc/bridge', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cameraId: camera.id, streamId })
    });
    if (!bridgeRes.ok) { const e = await bridgeRes.json().catch(() => ({})); throw new Error(e.error || 'Bridge failed: ' + bridgeRes.status); }
    if (!mountedRef.current) return;
    setStatus('negotiating');

    let iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
    try {
      const t = await fetch('/api/turn/credentials', { credentials: 'include' });
      if (t.ok) { const d = await t.json(); if (d.iceServers) iceServers = d.iceServers; }
    } catch(e) {}

    const wsProto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${wsProto}://${window.location.host}/ws/milestone-signal?streamId=${streamId}`);
    wsRef.current = ws;
    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;

    pc.onicecandidate = e => {
      if (e.candidate && ws.readyState === WebSocket.OPEN)
        ws.send(JSON.stringify({ type: 'webrtc_ice_candidate', candidate: e.candidate.candidate }));
    };
    pc.ontrack = e => {
      if (!mountedRef.current) return;
      if (videoRef.current && e.streams[0]) {
        videoRef.current.srcObject = e.streams[0];
        setStatus('playing');
        // Capture thumbnail frame once video has actual frame data
        const captureFrame = () => {
          const video = videoRef.current;
          if (!video || !video.videoWidth || !video.videoHeight) {
            setTimeout(captureFrame, 500);
            return;
          }
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d').drawImage(video, 0, 0);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            if (dataUrl && dataUrl.length > 1000) {
              useStore.getState().setMilestoneThumbnail(camera.id, dataUrl);
              console.log('[MilestonePlayer] Thumbnail captured for', camera.id);
            } else {
              setTimeout(captureFrame, 500);
            }
          } catch(err) { console.warn('[MilestonePlayer] Capture failed:', err); }
        };
        setTimeout(captureFrame, 1000);
      }
    };
    pc.onconnectionstatechange = () => {
      if ((pc.connectionState === 'failed' || pc.connectionState === 'disconnected') && mountedRef.current) {
        setStatus('error'); setError('Connection lost'); if (onFallback) onFallback();
      }
    };
    ws.onmessage = async (event) => {
      if (!mountedRef.current) return;
      try {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'webrtc_offer':
            await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: msg.sdp }));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            ws.send(JSON.stringify({ type: 'webrtc_answer', sdp: answer.sdp }));
            break;
          case 'webrtc_ice_candidate':
            if (msg.candidate) {
              try {
                const candidateInit = {
                  candidate: msg.candidate,
                  sdpMid: msg.sdpMid ?? '0',
                  sdpMLineIndex: msg.sdpMLineIndex ?? 0,
                };
                await pc.addIceCandidate(new RTCIceCandidate(candidateInit));
              } catch(e) { console.error('[MilestonePlayer] addIceCandidate failed:', e.message, msg.candidate); }
            }
            break;
          case 'webrtc_stopped':
            if (mountedRef.current) { setStatus('error'); setError(msg.reason || 'Stream stopped'); if (onFallback) onFallback(); }
            break;
        }
      } catch(e) {}
    };
    ws.onerror = () => { if (mountedRef.current) { setStatus('error'); setError('Signaling failed'); if (onFallback) onFallback(); } };
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
      <video ref={videoRef} autoPlay playsInline muted
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: status === 'playing' ? 'block' : 'none' }} />
      {status === 'connecting' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
          <div className="loading-spinner" style={{ marginRight: 8 }} />Connecting...
        </div>
      )}
      {status === 'negotiating' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
          <div className="loading-spinner" style={{ marginRight: 8 }} />Negotiating...
        </div>
      )}
      {status === 'error' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#ef4444', gap: 4 }}>
          <AlertCircle size={24} />
          <span style={{ fontSize: 12 }}>{error || 'Stream failed'}</span>
        </div>
      )}
    </div>
  );
};

export default MilestonePlayer;
